import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePatients } from '@/hooks/usePatients';
import { Combobox } from '@/v2/design-system/primitives';

/**
 * Story 45.4 (G-BUSCA) — busca global de paciente do Topbar. Substitui o input
 * morto por busca real sobre GET /patients?search= (nome/telefone/CPF, escopo
 * OwnDataInterceptor herdado): debounce 300ms, mínimo 2 caracteres (abaixo disso
 * a query NEM dispara), seleção navega direto para a ficha e Enter sem resultado
 * cai na lista completa filtrada — caminho determinístico, nunca beco sem saída.
 * Padrão: PatientCombobox (43.2) adaptado para navegação.
 */
const MIN_CHARS = 2;

export function GlobalPatientSearch({
  id,
  className,
  autoFocus = false,
  onNavigate,
}: {
  id: string;
  className?: string;
  autoFocus?: boolean;
  onNavigate?: () => void;
}) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 300);
    return () => clearTimeout(t);
  }, [term]);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  const enabled = debounced.length >= MIN_CHARS;
  const { data, isFetching } = usePatients(
    { search: debounced, limit: 8, status: 'active' },
    { enabled },
  );

  const options = useMemo(
    () =>
      enabled
        ? (data?.data ?? []).map((patient) => ({
            value: patient.id,
            label: patient.name,
            description: patient.phone || undefined,
          }))
        : [],
    [data, enabled],
  );

  const finish = () => {
    setTerm('');
    setOpen(false);
    onNavigate?.();
  };

  const goToPatient = (patientId: string) => {
    finish();
    navigate(`/pacientes/${patientId}`);
  };

  const goToList = () => {
    const q = term.trim();
    finish();
    navigate(`/pacientes?search=${encodeURIComponent(q)}`);
  };

  return (
    <div
      className={className}
      onKeyDownCapture={(e) => {
        // Fallback determinístico: Enter com termo válido e nenhum resultado → lista filtrada.
        if (e.key === 'Enter' && term.trim().length >= MIN_CHARS && options.length === 0) {
          e.preventDefault();
          goToList();
        }
      }}
    >
      <Combobox
        ref={inputRef}
        id={id}
        inputValue={term}
        onInputValueChange={(text) => {
          setTerm(text);
          const valid = text.trim().length >= MIN_CHARS;
          if (valid && !open) setOpen(true);
          if (!valid && open) setOpen(false);
        }}
        options={options}
        onSelect={(option) => goToPatient(option.value)}
        open={open && enabled}
        onOpenChange={setOpen}
        loading={enabled && (isFetching || term.trim() !== debounced)}
        loadingMessage="Buscando pacientes…"
        emptyMessage="Nenhuma paciente encontrada — Enter abre a lista completa"
        placeholder="Buscar paciente…"
        controlSize="dense"
      />
    </div>
  );
}
